import { Callout, getTheme, mergeStyleSets } from "@fluentui/react";
import {
  Breadcrumb,
  BreadcrumbButton,
  BreadcrumbItem,
} from "@fluentui/react-components";
import { useBoolean, useId } from "@fluentui/react-hooks";
import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AmdDownIcon } from "../../../components/common/icons/down/Down";
import { AmdUpIcon } from "../../../components/common/icons/up/Up";
import { $black } from "../../../cssjs/colors";
import { default as PageEnum } from "../../../enum/PageEnum";
import { airportCodeActions } from "../../../store/airport/AirportCodeSlice";
import { useAppDispatch } from "../../../store/StoreHooks";
import { getAirportConfiguration } from "../../../utils/common/AirportConfiguration";
import {
  getBreadcrumbPageList,
  isValidInternalUrl,
} from "../../../utils/common/CommonHelperFunctions";

interface ICustomBreadcrumbProps {
  handleMapView: (e: any) => void;
  airportCode: string;
  screenName: string;
  setSelectedFlight: (flight: {
    flightID: string | undefined;
    flightDocID: string | undefined;
  }) => void;
}

const CustomBreadcrumb: React.FunctionComponent<ICustomBreadcrumbProps> = ({
  handleMapView,
  airportCode,
  screenName,
  setSelectedFlight,
}) => {
  const navigate = useNavigate();
  const [airportOptions, setAirportOptions] = React.useState<any[]>([]);
  const airportConfigurations = getAirportConfiguration();
  const breadcrumbUrls = getBreadcrumbPageList(airportCode);

  const airportButtonId = useId("airport-button");
  const screenButtonId = useId("screen-button");

  const isMultiAirport = location?.pathname?.startsWith("/multi-airports");
  const [isAirportCalloutVisible, { toggle: toggleIsAirportCalloutVisible }] =
    useBoolean(false);
  const [isScreenCalloutVisible, { toggle: toggleIsScreenCalloutVisible }] =
    useBoolean(false);

  const dispatch = useAppDispatch();

  useEffect(() => {
    const options = airportConfigurations.map((airport: any) => ({
      key: `/airportlist/${airport.iataCode.toLowerCase()}`,
      text: airport.iataCode,
    }));
    setAirportOptions(options);
  }, [airportConfigurations]);

  return (
    <Breadcrumb aria-label="Overview Breadcrumbs">
      <BreadcrumbItem>
        <Link to={PageEnum.PageUrl.MultiAirports} className={`text-d-none`}>
          <h1
            className={`${contentStyles.airportHeading} text-d-none fw-500 fs-16`}
            data-testid="multi-airport"
          >
            Airports list
          </h1>
        </Link>
      </BreadcrumbItem>
      <BreadcrumbItem>
        {!isMultiAirport && (
          <>
            /
            <BreadcrumbButton
              className={contentStyles.calloutButton}
              onClick={toggleIsAirportCalloutVisible}
              id={airportButtonId}
            >
              <div className={contentStyles.menuDropdown}>
                {airportCode}

                {isAirportCalloutVisible ? (
                  <AmdUpIcon
                    className={`${contentStyles.upIcon} float-l mt-1`}
                    color={$black}
                  />
                ) : (
                  <AmdDownIcon
                    className={`${contentStyles.downIcon} float-l`}
                    color={$black}
                  />
                )}
              </div>
            </BreadcrumbButton>
            {isAirportCalloutVisible && (
              <Callout
                target={`#${airportButtonId}`}
                onDismiss={toggleIsAirportCalloutVisible}
                setInitialFocus
              >
                {airportOptions.map((option: any) => (
                  <div
                    key={option.key}
                    onClick={() => {
                      const airportCode = option.text;
                      dispatch(
                        airportCodeActions.setAirportCode({ airportCode })
                      );
                      setSelectedFlight({
                        flightDocID: undefined,
                        flightID: undefined,
                      });
                      sessionStorage.setItem("airportCode", airportCode);
                      toggleIsAirportCalloutVisible();
                      const isValidPage = breadcrumbUrls.some(
                        (page) => page.url === location.pathname
                      );
                      if (!isValidPage) {
                        navigate(`/${PageEnum.PageUrl.Home}`);
                      }
                    }}
                    style={{
                      padding: "8px",
                      cursor: "pointer",
                      pointerEvents: "auto",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#eaeaea";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }}
                  >
                    {option?.text}
                  </div>
                ))}
              </Callout>
            )}
          </>
        )}
      </BreadcrumbItem>
      <BreadcrumbItem>
        {!isMultiAirport && (
          <>
            {screenName ? "/" : ""}
            <BreadcrumbButton
              className={contentStyles.calloutButton}
              onClick={toggleIsScreenCalloutVisible}
              id={screenButtonId}
            >
              {screenName && (
                <div className={contentStyles.menuDropdown}>
                  {screenName}
                  {isScreenCalloutVisible ? (
                    <AmdUpIcon
                      className={`${contentStyles.upIcon} float-l mt-1`}
                      color={$black}
                    />
                  ) : (
                    <AmdDownIcon
                      className={`${contentStyles.downIcon} float-l`}
                      color={$black}
                    />
                  )}
                </div>
              )}
            </BreadcrumbButton>
            {isScreenCalloutVisible && (
              <Callout
                target={`#${screenButtonId}`}
                onDismiss={toggleIsScreenCalloutVisible}
                setInitialFocus
              >
                {breadcrumbUrls.map((option: any) => {
                  const safeUrl = isValidInternalUrl(option.url)
                    ? option.url
                    : `/${PageEnum.PageUrl.Home}`;

                  return (
                    <Link
                      key={option.key}
                      to={safeUrl}
                      className={`text-d-none`}
                      onClick={(e: any) => {
                        if (isValidInternalUrl(option.url)) {
                          toggleIsScreenCalloutVisible();
                          handleMapView(e);
                        } else {
                          e.preventDefault();
                          console.warn(
                            "Blocked navigation to untrusted URL:",
                            option.url
                          );
                        }
                      }}
                    >
                      <div
                        style={{
                          padding: "8px",
                          cursor: "pointer",
                          color: $black,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#eaeaea";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "transparent";
                        }}
                      >
                        {option?.text}
                      </div>
                    </Link>
                  );
                })}
              </Callout>
            )}
          </>
        )}
      </BreadcrumbItem>
    </Breadcrumb>
  );
};

export default CustomBreadcrumb;

const theme = getTheme();
const contentStyles = mergeStyleSets({
  menuDropdown: {
    display: "flex",
    fontSize: "15px",
    color: "#000835",
    padding: "0 0 0 2px",
    fontWeight: 600,
    "&:hover": {
      color: "#0C66E1",
    },
  },
  upIcon: {
    paddingLeft: "1px !important",
  },
  downIcon: {
    paddingLeft: "0px !important",
    paddingTop: "2px !important",
  },
  airportHeading: {
    color: "#000835",
    "&:hover": {
      color: "#0C66E1 !important",
    },
  },
  calloutButton: {
    apperence: "transparent",
    margin: "0px 2px 0px 0px !important",
    "&.fui-Button": {
      paddingLeft: "0px",
      paddingRight: "0px",
      paddingTop: "0px",
      paddingBottom: "0px",
    },

    ":hover": {
      background: `${theme.palette.blueMid}`,
    },
    flexDirection: "row-reverse",
  },
  buttonArea: {
    textAlign: "center",
    borderRadius: "4px",
    ":hover": {
      background: `#c5d5fa`,
    },
  },
  breadcrumb: {
    flexDirection: "row-reverse",
    display: "flex",
    "& .ms-Dropdown-title": {
      background: "none",
      border: "none",
    },
    "&.is-active .ms-Dropdown-title": {
      border: "none",
    },
  },
});
